import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, RotateCcw, Mail, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const SUPPORT_EMAIL = "contact@misyou.in";
const SUPPORT_PHONE = "+91 8146499739";
const EFFECTIVE_DATE = "13 May 2026";

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <RotateCcw className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Refund & Cancellation Policy</h1>
          <p className="text-muted-foreground">Effective Date: {EFFECTIVE_DATE}</p>
        </div>

        <Card className="shadow-lg border-slate-100">
          <CardContent className="p-6 sm:p-10 prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600">
              MISYOU ("we", "our", or "us") is committed to a fair, transparent refund policy. This
              page explains when and how you can cancel your subscription, request a refund, and what
              to expect after you do.
            </p>

            <h2>1. Subscription Overview</h2>
            <p>
              MISYOU offers a <strong>15-day free trial</strong> when you create a new missing-person
              case. After the trial, paid subscriptions are available to keep your case photos visible
              and to avoid auto-deletion. Pricing is shown on the Subscription page and confirmed
              before payment.
            </p>

            <h2>2. 7-Day Money-Back Guarantee (First-Time Subscribers Only)</h2>
            <p>
              If you are subscribing to MISYOU <strong>for the first time</strong> and you are not
              satisfied with the service, you may request a full refund within <strong>7 calendar
              days</strong> of your initial payment, no questions asked.
            </p>
            <ul>
              <li>Applies once per user account.</li>
              <li>Applies only to the very first paid subscription on the account.</li>
              <li>Refund will be issued to the original payment method used at checkout.</li>
              <li>Refunds typically reach your bank/UPI within 5–10 business days, depending on your bank or card issuer.</li>
            </ul>

            <h2>3. Cancellation</h2>
            <p>
              You may cancel your active subscription at any time from your Dashboard. Upon cancellation:
            </p>
            <ul>
              <li>Your subscription will remain active for the days already paid for. There is no automatic auto-renewal — MISYOU subscriptions are paid for fixed durations selected at checkout (e.g., 7, 15, 30, 60 days).</li>
              <li>You will not be charged again unless you start a new subscription.</li>
              <li>After the paid period ends, your case will follow our standard lifecycle (15-day grace blur followed by auto-deletion if not renewed).</li>
            </ul>

            <h2>4. Non-Refundable Cases</h2>
            <p>Outside the 7-day money-back window, payments are <strong>non-refundable</strong>, including but not limited to:</p>
            <ul>
              <li>Renewal payments and any subsequent (second, third, etc.) subscription payments on the same account.</li>
              <li>Partial / prorated refunds for unused days within an already-paid subscription period.</li>
              <li>Cases where the missing person has been located before the subscription period ends.</li>
              <li>Subscriptions purchased and then voluntarily abandoned without contacting us within the 7-day window.</li>
            </ul>
            <p className="text-sm text-slate-600">
              We retain the right to make exceptions in cases of genuine hardship or service-side issues
              caused by MISYOU. Contact us and we will review on a case-by-case basis.
            </p>

            <h2>5. Failed, Duplicate or Fraudulent Charges</h2>
            <ul>
              <li>If a payment is debited from your account but your subscription is not activated, contact us within 7 days with the transaction reference. We will trace, refund, or manually activate as appropriate.</li>
              <li>If you see a duplicate charge for the same subscription, the duplicate will be refunded in full.</li>
              <li>If you believe a charge was made fraudulently, contact us immediately and also inform your bank / payment provider so a chargeback can be filed if needed.</li>
            </ul>

            <h2>6. How to Request a Refund or Cancellation</h2>
            <p>To request a refund or cancellation, contact our support team with the details below:</p>
            <ul>
              <li>Registered email address on your MISYOU account</li>
              <li>Order ID / payment transaction reference</li>
              <li>Reason for the request (optional, but helps us improve)</li>
              <li>Date of payment</li>
            </ul>
            <p>You can reach us via:</p>
            <ul>
              <li>
                <Mail className="inline w-4 h-4 mr-1 -mt-0.5" />
                Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-medium">{SUPPORT_EMAIL}</a>
              </li>
              <li>
                <Phone className="inline w-4 h-4 mr-1 -mt-0.5" />
                Customer Care: <a href="tel:+918146499739" className="text-primary font-medium">{SUPPORT_PHONE}</a> (Mon–Sat, 10am–7pm IST)
              </li>
            </ul>

            <h2>7. Response Time</h2>
            <p>
              We acknowledge every refund request within <strong>2 business days</strong> and aim to
              resolve eligible refund requests within <strong>5 business days</strong> of acknowledgement.
              The actual credit back to your bank/UPI/card may take an additional 3–7 business days
              depending on your payment provider.
            </p>

            <h2>8. Service Disruptions</h2>
            <p>
              If MISYOU is unable to deliver the subscription benefits you paid for (e.g., extended
              platform outage), we will either credit your account with equivalent additional days at
              no charge, or — at your option — provide a prorated refund for the affected period.
            </p>

            <h2>9. Changes to this Policy</h2>
            <p>
              We may update this Refund & Cancellation Policy from time to time. Material changes will
              be notified to registered users by email or in-app notification. Continued use of MISYOU
              after such changes constitutes acceptance of the revised policy.
            </p>

            <h2>10. Contact</h2>
            <p>For any questions about this policy:</p>
            <ul>
              <li>Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-medium">{SUPPORT_EMAIL}</a></li>
              <li>Customer Care: <a href="tel:+918146499739" className="text-primary font-medium">{SUPPORT_PHONE}</a></li>
              <li>Address: MISYOU, Punjab, India</li>
            </ul>
          </CardContent>
        </Card>

        <div className="text-center mt-10">
          <p className="text-sm text-muted-foreground">
            By subscribing to MISYOU, you acknowledge that you have read and agree to this Refund & Cancellation Policy.
          </p>
        </div>
      </main>
    </div>
  );
}
